/**
 * The town's building kit (lifted from lantern-water's `world.js`: its
 * material library, its `mesh`, `box`, `cylinder`, `line`, `contact`, `glow`,
 * `lantern`, `windowFrame`, `roof`, `hipRoof`, `archShape`, `flowers`, `pot`,
 * `barrel` and `crate` helpers, and the shader additions the materials carry:
 * the paving's world-scale projection, the reflected-water shimmer of
 * `water-light.js`, the breeze on leaves and pennants). One kit per view:
 * every material and texture here lives as long as the view (or until a lost
 * context), and every prefab of every scene shares them, so the static
 * batcher (`batch.ts`) merges their geometry by material.
 *
 * Prefabs (`prefabs/<id>.ts`) build with a `Ctx`: the kit, the helpers bound
 * to it, and a random stream seeded from the placement's id (never a shared
 * stream: one prefab's draws cannot move another's). Builders work in
 * three.js's axes inside their own root (y up; +z is south, toward the
 * camera; a prefab's front faces +z, as the prototype's do).
 *
 * The prototype's helpers also registered colliders, walking surfaces,
 * interactions, chimneys and point lights while they built; here the scene
 * record holds all of those (ADR 0031: one home per fact), so the helpers
 * only make geometry. Glows (the prototype's `THREE.Sprite` halos) are marker
 * objects collected into one instanced draw (`glows.ts`).
 */
import * as THREE from 'three';
import { seededRandom } from '../grid.ts';
import { type ShaderHook, addShaderHook } from '../material-hooks.ts';
import { type FacadeAtlas, type SurfaceKind, type WindowAtlas, contactTexture, facadeAtlas, foliageTexture, glowTexture, lanternPaneTexture, paintRelief, pennantTexture, reliefTexture, surfaceKinds, texture, windowAtlas } from './art.ts';
import { floweringPlant, terracottaPlanter } from './botanical.ts';
import { sceneLook } from './constants.ts';

export type SceneMaterials = {
  readonly stone: THREE.MeshStandardMaterial;
  readonly paving: THREE.MeshStandardMaterial;
  readonly roof: THREE.MeshStandardMaterial;
  readonly roofDark: THREE.MeshStandardMaterial;
  readonly wood: THREE.MeshStandardMaterial;
  readonly darkWood: THREE.MeshStandardMaterial;
  readonly plaster: THREE.MeshStandardMaterial;
  readonly limestone: THREE.MeshStandardMaterial;
  readonly canopy: THREE.MeshStandardMaterial;
  readonly peach: THREE.MeshStandardMaterial;
  readonly cream: THREE.MeshStandardMaterial;
  readonly teal: THREE.MeshStandardMaterial;
  readonly iron: THREE.MeshStandardMaterial;
  readonly brass: THREE.MeshStandardMaterial;
  readonly grass: THREE.MeshStandardMaterial;
  readonly rug: THREE.MeshStandardMaterial;
  readonly glass: THREE.MeshStandardMaterial;
  readonly lanternGlass: THREE.MeshBasicMaterial;
  readonly windowGlass: THREE.MeshStandardMaterial;
  readonly windowCool: THREE.MeshStandardMaterial;
  readonly dark: THREE.MeshStandardMaterial;
  readonly red: THREE.MeshStandardMaterial;
  readonly canvas: THREE.MeshStandardMaterial;
  readonly moss: THREE.MeshStandardMaterial;
  readonly book1: THREE.MeshStandardMaterial;
  readonly book2: THREE.MeshStandardMaterial;
  readonly book3: THREE.MeshStandardMaterial;
  readonly bottle: THREE.MeshStandardMaterial;
  readonly bottle2: THREE.MeshStandardMaterial;
};

/** The uniforms every animated surface shares; the view writes them once a frame. */
export type SharedUniforms = {
  readonly breezeTime: THREE.IUniform<number>;
  readonly breezeAmount: THREE.IUniform<number>;
  readonly waterTime: THREE.IUniform<number>;
  readonly waterNight: THREE.IUniform<number>;
  readonly caustics: THREE.IUniform<THREE.Texture>;
  /** 1 at Medium: stone and timber by the canal shimmer; 0 at Off. */
  readonly shimmer: THREE.IUniform<number>;
  /** The paving's damp bands along the quays (x of the two canal edges). */
  readonly damp: THREE.IUniform<THREE.Vector2>;
};

/** The surface textures a scene may ship (its asset slots) and the kit's map each one replaces. */
export const artworkSlots = { paving: 'cobble', limestone: 'stone', slate_roof: 'slate', terracotta_roof: 'roof', rug: 'rug' } as const satisfies Readonly<Record<string, SurfaceKind>>;
export type ArtworkSlot = keyof typeof artworkSlots;

export type Kit = {
  readonly m: SceneMaterials;
  readonly maps: Readonly<Record<SurfaceKind, THREE.CanvasTexture>>;
  readonly relief: Readonly<Record<SurfaceKind, THREE.CanvasTexture>>;
  readonly facades: FacadeAtlas;
  readonly facadeMaterial: THREE.MeshStandardMaterial;
  readonly leaves: readonly [THREE.MeshStandardMaterial, THREE.MeshStandardMaterial];
  readonly pennant: THREE.MeshStandardMaterial;
  readonly gable: THREE.MeshStandardMaterial;
  readonly botanical: THREE.MeshStandardMaterial;
  readonly contact: THREE.MeshBasicMaterial;
  readonly glowMap: THREE.Texture;
  readonly windows: { readonly warm: WindowAtlas; readonly cool: WindowAtlas };
  readonly geo: { readonly cube: THREE.BoxGeometry; readonly plane: THREE.PlaneGeometry; readonly cyl: THREE.CylinderGeometry; readonly sphere: THREE.IcosahedronGeometry };
  readonly uniforms: SharedUniforms;
  /** A material, texture or geometry made once per kit under `key` (a module's own shared finish), disposed with the kit. */
  shared<T extends THREE.Material | THREE.Texture | THREE.BufferGeometry>(key: string, make: () => T): T;
  /**
   * A finish derived from a kit material (a clone made once under `key`, `setup` changing what it changes and
   * adding its own hooks). The kit keeps it in step with its base: a colour or bump scale `setup` left as the
   * base's follows the base when shipped artwork lands (`world.js` made these after the artwork loaded, so they
   * took the artwork's settings), and the maps are the base's own textures.
   */
  derive(key: string, base: THREE.MeshStandardMaterial, setup: (material: THREE.MeshStandardMaterial) => void): THREE.MeshStandardMaterial;
  /** The reflected-water shimmer every canal-side stone and timber carries (`water-light.js` `installWaterShimmer`). */
  readonly shimmerHook: ShaderHook;
  /** The double-sided copy of a roof material that hip roofs share (`world.js` `hipRoofMaterials`). */
  hipRoof(material: THREE.Material): THREE.Material;
  /** Puts a shipped texture (a scene's asset) in place of the procedural fallback; the relief follows it. */
  applyArtwork(slot: ArtworkSlot | 'caustics', image: ImageBitmap | HTMLImageElement): void;
  /** Which surfaces show shipped artwork (the rest, the procedural fallback). */
  artwork(): Readonly<Partial<Record<ArtworkSlot | 'caustics', boolean>>>;
  dispose(): void;
};

export type Vec3 = readonly [number, number, number];

/** What a prefab builds with: the kit, its helpers, the placement's parameters and its own random stream. */
export type Ctx = {
  readonly kit: Kit;
  readonly m: SceneMaterials;
  readonly random: () => number;
  readonly params: Readonly<Record<string, number | boolean | string>>;
  readonly plane: THREE.PlaneGeometry;
  readonly sphere: THREE.IcosahedronGeometry;
  readonly cube: THREE.BoxGeometry;
  readonly cyl: THREE.CylinderGeometry;
  readonly leafMats: readonly [THREE.Material, THREE.Material];
  readonly mesh: (p: THREE.Object3D, geo: THREE.BufferGeometry, m: THREE.Material, x?: number, y?: number, z?: number, sx?: number, sy?: number, sz?: number) => THREE.Mesh;
  readonly box: (p: THREE.Object3D, x: number, y: number, z: number, w: number, h: number, d: number, m?: THREE.Material) => THREE.Mesh;
  readonly cylinder: (p: THREE.Object3D, x: number, y: number, z: number, rad: number, h: number, m?: THREE.Material, top?: number) => THREE.Mesh;
  readonly line: (p: THREE.Object3D, a: Vec3, b: Vec3, width: number, m?: THREE.Material) => THREE.Mesh;
  /** A soft additive halo (one instance of the glow draw). */
  readonly glow: (p: THREE.Object3D, x: number, y: number, z: number, size?: number, opacity?: number) => void;
  readonly lantern: (p: THREE.Object3D, x: number, y: number, z: number, post?: boolean) => void;
  readonly windowFrame: (p: THREE.Object3D, x: number, y: number, z: number, width?: number, height?: number, shutters?: boolean, glass?: THREE.Material) => void;
  readonly roof: (p: THREE.Object3D, x: number, y: number, z: number, width: number, depth: number, rise: number, m?: THREE.Material) => void;
  readonly hipRoof: (p: THREE.Object3D, x: number, y: number, z: number, width: number, depth: number, rise: number, material: THREE.Material) => void;
  readonly archShape: (width: number, height: number) => THREE.Shape;
  /** A flowering plant; its seed comes from where it stands (the prototype's positional seed). */
  readonly flowers: (p: THREE.Object3D, x: number, y: number, z: number, width?: number, count?: number, trailing?: boolean | number) => void;
  readonly pot: (p: THREE.Object3D, x: number, z: number, y?: number, scale?: number) => void;
  readonly barrel: (p: THREE.Object3D, x: number, z: number, size?: number, y?: number) => void;
  readonly crate: (p: THREE.Object3D, x: number, z: number, size?: number, y?: number) => void;
  /** Marks a mesh's geometry as one the leader's cutaway may open (the eastern house). */
  readonly cutaway: (o: THREE.Object3D) => void;
};

const look = sceneLook.materials;

// ---------------------------------------------------------------- shader additions

const pavingVertex = (worldSize: number): string => `
abGroundWorld=(modelMatrix*vec4(transformed,1.)).xyz;
vec3 abPavingNormal=abs(normalize(mat3(modelMatrix)*normal));
vec2 abPavingUv=abPavingNormal.y>.5?abGroundWorld.xz:abPavingNormal.x>abPavingNormal.z?abGroundWorld.zy:abGroundWorld.xy;
#ifdef USE_MAP
vMapUv=abPavingUv/${worldSize.toFixed(3)};
#endif
#ifdef USE_BUMPMAP
vBumpMapUv=abPavingUv/${worldSize.toFixed(3)};
#endif`;

const pavingFragment = `
float abWear=sin(abGroundWorld.x*.71+sin(abGroundWorld.z*.48))*sin(abGroundWorld.z*.31)*.075;
float abDampBand=exp(-pow((abGroundWorld.x-abDamp.x)*1.8,2.))+exp(-pow((abGroundWorld.x-abDamp.y)*1.6,2.));
diffuseColor.rgb*=.96+abWear-abDampBand*.18;
diffuseColor.rgb=mix(diffuseColor.rgb,diffuseColor.rgb*vec3(.75,.89,.76),clamp(abDampBand*.3,0.,.35));`;

/**
 * The canal's banks in GLSL (`water-light.js` `coastGlsl`, the same curve as
 * the river's course in `water.ts`): x is the west bank, y the east.
 */
export const coastGlsl = (west: number, east: number): string => `
float lwSmooth(float v,float a,float b){float t=clamp((v-a)/(b-a),0.,1.);return t*t*(3.-2.*t);}
vec2 lwCoast(float z){
  float south=lwSmooth(z,16.,48.),north=lwSmooth(-z,34.,78.);
  float bend=sin(z*.12)*south*2.+sin((-z-34.)*.055)*north*3.;
  return vec2(${west.toFixed(3)}-south*31.2-north*20.2+bend,${east.toFixed(3)}+south*29.+north*17.+bend);
}`;

const shimmerFragment = (waterY: number): string => {
  const s = sceneLook.shimmer;
  const v3 = (a: readonly number[]): string => `vec3(${a.map((n) => n.toFixed(3)).join(',')})`;
  return `
{
  vec3 lwN=normalize((vec4(normal,0.)*viewMatrix).xyz);
  float lwAbove=lwShimmerWorld.y-(${waterY.toFixed(3)});
  vec2 lwBank=lwCoast(lwShimmerWorld.z);
  float lwOut=max(max(lwBank.x-lwShimmerWorld.x,lwShimmerWorld.x-lwBank.y),0.);
  if(lwShimmerOn>.5&&lwAbove>-.08&&lwAbove<3.2&&lwOut<${s.reach.toFixed(3)}){
    float lwSide=lwShimmerWorld.x<(lwBank.x+lwBank.y)*.5?1.:-1.;
    float lwFacing=clamp(lwN.x*lwSide,0.,1.)*(1.-abs(lwN.y));
    float lwUnder=clamp(-lwN.y,0.,1.);
    lwFacing=max(lwFacing,abs(lwN.z)*(1.-abs(lwN.y))*step(lwOut,.001));
    float lwWeight=max(lwFacing,lwUnder);
    if(lwWeight>.01){
      vec3 lwQ=floor(lwShimmerWorld*${s.pixels.toFixed(1)})/${s.pixels.toFixed(1)};
      float lwAlong=abs(lwN.x)>abs(lwN.z)?lwQ.z:lwQ.x;
      vec2 lwUv=lwUnder>.5?lwQ.xz*vec2(.42,.2):vec2(lwAlong*.085,lwQ.y*.6);
      float lwT=lwWaterTime;
      float lwA=textureLod(lwCaustics,lwUv+vec2(lwT*.021,lwT*.047),1.6).r;
      float lwB=textureLod(lwCaustics,lwUv*1.31+vec2(.37-lwT*.017,.11+lwT*.033),1.6).r;
      float lwC=smoothstep(.1,.5,min(lwA,lwB)+lwA*lwB*.3);
      float lwFade=smoothstep(-.08,.06,lwAbove)*exp(-lwAbove*mix(${s.wallFade.toFixed(3)},${s.undersideFade.toFixed(3)},lwUnder));
      float lwLap=.06*sin(lwT*1.1+lwQ.z*1.7+lwQ.x)+.04*sin(lwT*1.9-lwQ.z*3.1);
      float lwWet=(1.-smoothstep(.1,.3,lwAbove-lwLap))*lwFacing;
      diffuseColor.rgb*=1.-lwWet*.32;
      roughnessFactor=mix(roughnessFactor,.38,lwWet*.8);
      vec3 lwTint=mix(${v3(s.color)}*${s.strength.toFixed(3)},${v3(s.nightColor)}*${s.nightStrength.toFixed(3)},lwWaterNight);
      totalEmissiveRadiance+=diffuseColor.rgb*lwTint*lwC*lwFade*lwWeight*(1.-lwWet*.6);
    }
  }
}`;
};

const leafBreeze = `
float abWeight=clamp(uv.y,0.,1.);
float abPhase=position.x*.41+position.z*.29;
transformed.x+=(sin(breezeTime*.82+abPhase)*.075+sin(breezeTime*.27+abPhase*.4)*.035)*abWeight*breezeAmount;
transformed.z+=sin(breezeTime*.61+abPhase*1.3)*.035*abWeight*breezeAmount;
transformed.y+=sin(breezeTime*1.1+abPhase)*.009*abWeight*breezeAmount;`;

const pennantBreeze = `
float abFreeEdge=pow(1.-uv.y,2.);
transformed.z+=sin(breezeTime*1.15+position.x*.8+uv.y*4.)*.075*abFreeEdge*breezeAmount;
transformed.x+=sin(breezeTime*.87+position.x)*.018*abFreeEdge*breezeAmount;`;

/** Stable 32-bit seed of a placement id (FNV-1a), so a prefab's random stream follows its id, not where it stands. */
export const seedOf = (id: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i += 1) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
};

export type KitOptions = {
  /** The canal's banks and its water line (the shimmer's reach), from the scene's terrain and water. */
  readonly coast: { readonly west: number; readonly east: number; readonly waterY: number };
};

export const createKit = (options: KitOptions): Kit => {
  const owned: (THREE.Material | THREE.Texture | THREE.BufferGeometry)[] = [];
  const keep = <T extends THREE.Material | THREE.Texture | THREE.BufferGeometry>(x: T): T => {
    owned.push(x);
    return x;
  };
  const maps = Object.fromEntries(surfaceKinds.map((k) => [k, keep(texture(k))])) as Record<SurfaceKind, THREE.CanvasTexture>;
  const relief = Object.fromEntries(surfaceKinds.map((k) => [k, keep(reliefTexture(maps[k]))])) as Record<SurfaceKind, THREE.CanvasTexture>;
  const windows = { warm: windowAtlas(), cool: windowAtlas(true) };
  for (const w of [windows.warm, windows.cool]) owned.push(w.map, w.emission);
  const lanternPane = keep(lanternPaneTexture());
  const caustics = keep(new THREE.Texture());
  caustics.wrapS = THREE.RepeatWrapping;
  caustics.wrapT = THREE.RepeatWrapping;
  caustics.colorSpace = THREE.NoColorSpace;
  caustics.minFilter = THREE.LinearMipmapLinearFilter;
  caustics.magFilter = THREE.LinearFilter;
  // A flat grey stands in until the shipped caustics land (the prototype's "flat light" fallback).
  const blank = document.createElement('canvas');
  blank.width = 4;
  blank.height = 4;
  const blankCtx = blank.getContext('2d');
  if (blankCtx !== null) {
    blankCtx.fillStyle = '#404040';
    blankCtx.fillRect(0, 0, 4, 4);
  }
  caustics.image = blank;
  caustics.needsUpdate = true;
  const uniforms: SharedUniforms = {
    breezeTime: { value: 0 },
    breezeAmount: { value: 1 },
    waterTime: { value: 0 },
    waterNight: { value: 0 },
    caustics: { value: caustics },
    shimmer: { value: 1 },
    damp: { value: new THREE.Vector2(...look.pavingDamp) },
  };
  const bump = (kind: SurfaceKind | null): number => (kind === null ? look.bump.other : look.bump[kind]);
  const mat = (color: string, kind: SurfaceKind | null, extra: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial =>
    keep(
      new THREE.MeshStandardMaterial({
        color,
        map: kind === null ? null : maps[kind],
        roughness: look.roughness,
        bumpMap: kind === null ? null : relief[kind],
        bumpScale: bump(kind),
        ...extra,
      }),
    );
  const c = look.colors;
  const m: SceneMaterials = {
    stone: mat(c.stoneFallback, 'stone', { bumpScale: look.bump.stoneFallback }),
    paving: mat(c.paving, 'cobble'),
    roof: mat(c.roof, 'roof'),
    roofDark: mat(c.roofDark, 'slate'),
    wood: mat(c.wood, 'wood'),
    darkWood: mat(c.darkWood, 'wood'),
    plaster: mat(c.plaster, 'plaster'),
    limestone: mat(c.limestone, 'plaster'),
    canopy: mat(c.canopy, 'plaster'),
    peach: mat(c.peach, 'plaster'),
    cream: mat(c.cream, 'plaster'),
    teal: mat(c.teal, 'wood'),
    iron: mat(c.iron, null),
    brass: mat(c.brass, null, { metalness: 0.5, roughness: 0.42 }),
    grass: mat(c.grass, 'grass'),
    rug: mat(c.rug, 'rug'),
    glass: mat(c.glass, null, { emissive: c.glassEmissive, emissiveIntensity: 0.9, roughness: 0.3 }),
    lanternGlass: keep(new THREE.MeshBasicMaterial({ color: c.lanternGlass, map: lanternPane, vertexColors: true })),
    windowGlass: mat(c.windowGlass, null, { map: windows.warm.map, emissive: c.windowGlassEmissive, emissiveMap: windows.warm.emission, emissiveIntensity: 0.35, roughness: 0.5 }),
    windowCool: mat(c.windowCool, null, { map: windows.cool.map, emissive: c.windowCoolEmissive, emissiveMap: windows.cool.emission, emissiveIntensity: 0.08, roughness: 0.45 }),
    dark: mat(c.dark, null),
    red: mat(c.red, null),
    canvas: mat(c.canvas, null),
    moss: mat(c.moss, null),
    book1: mat(c.book1, null),
    book2: mat(c.book2, null),
    book3: mat(c.book3, null),
    bottle: mat(c.bottle, null, { metalness: 0.15, roughness: 0.25 }),
    bottle2: mat(c.bottle2, null, { metalness: 0.1, roughness: 0.28 }),
  };
  for (const [key, material] of Object.entries(m)) material.name = `scene-${key}`;
  const facades = facadeAtlas();
  owned.push(facades.map, facades.heightMap);
  const facadeMaterial = keep(new THREE.MeshStandardMaterial({ map: facades.map, bumpMap: facades.heightMap, bumpScale: look.bump.facade, color: '#ffffff', vertexColors: true, roughness: look.facadeRoughness }));
  const shimmerHook: ShaderHook = {
    key: 'town-shimmer',
    uniforms: { lwCaustics: uniforms.caustics, lwWaterTime: uniforms.waterTime, lwWaterNight: uniforms.waterNight, lwShimmerOn: uniforms.shimmer },
    vertexHeader: 'varying vec3 lwShimmerWorld;',
    fragmentHeader: `varying vec3 lwShimmerWorld;uniform sampler2D lwCaustics;uniform float lwWaterTime,lwWaterNight,lwShimmerOn;${coastGlsl(options.coast.west, options.coast.east)}`,
    vertex: [{ chunk: 'worldpos_vertex', after: 'lwShimmerWorld=(modelMatrix*vec4(transformed,1.)).xyz;' }],
    fragment: [{ chunk: 'emissivemap_fragment', after: shimmerFragment(options.coast.waterY) }],
  };
  addShaderHook(m.paving, {
    key: 'town-paving',
    uniforms: { abDamp: uniforms.damp },
    vertexHeader: 'varying vec3 abGroundWorld;',
    fragmentHeader: 'varying vec3 abGroundWorld;uniform vec2 abDamp;',
    vertex: [{ chunk: 'worldpos_vertex', after: pavingVertex(look.worldSize.paving) }],
    fragment: [{ chunk: 'color_fragment', after: pavingFragment }],
  });
  for (const material of [m.stone, m.cream, m.paving, m.wood, m.darkWood]) addShaderHook(material, shimmerHook);
  const breeze = { breezeTime: uniforms.breezeTime, breezeAmount: uniforms.breezeAmount };
  const leaves = [false, true].map((autumn) => {
    const leaf = keep(new THREE.MeshStandardMaterial({ map: keep(foliageTexture(autumn)), transparent: false, alphaTest: 0.45, side: THREE.DoubleSide, roughness: 1 }));
    addShaderHook(leaf, { key: 'town-leaf-breeze', uniforms: breeze, vertexHeader: 'uniform float breezeTime;uniform float breezeAmount;', vertex: [{ chunk: 'begin_vertex', after: leafBreeze }] });
    return leaf;
  }) as unknown as readonly [THREE.MeshStandardMaterial, THREE.MeshStandardMaterial];
  const pennant = keep(new THREE.MeshStandardMaterial({ map: keep(pennantTexture()), vertexColors: true, side: THREE.DoubleSide, roughness: 1 }));
  addShaderHook(pennant, { key: 'town-pennant-breeze', uniforms: breeze, vertexHeader: 'uniform float breezeTime,breezeAmount;', vertex: [{ chunk: 'begin_vertex', after: pennantBreeze }] });
  // Gable faces share one double-sided finish, so the batcher merges them (`world.js` `roofGableMaterial`).
  const gable = keep(m.plaster.clone());
  gable.side = THREE.DoubleSide;
  const botanical = keep(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.88, side: THREE.DoubleSide, forceSinglePass: true }));
  const contact = keep(new THREE.MeshBasicMaterial({ map: keep(contactTexture()), transparent: true, opacity: look.contactOpacity, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2 }));
  const glowMap = keep(glowTexture());
  const geo = {
    cube: keep(new THREE.BoxGeometry(1, 1, 1)),
    plane: keep(new THREE.PlaneGeometry(1, 1)),
    cyl: keep(new THREE.CylinderGeometry(1, 1, 1, 12)),
    sphere: keep(new THREE.IcosahedronGeometry(1, 1)),
  };
  const sharedThings = new Map<string, THREE.Material | THREE.Texture | THREE.BufferGeometry>();
  const hipRoofs = new Map<THREE.Material, THREE.Material>();
  const artwork: Partial<Record<ArtworkSlot | 'caustics', boolean>> = {};
  const derived: { readonly base: THREE.MeshStandardMaterial; readonly material: THREE.MeshStandardMaterial; readonly color: boolean; readonly bump: boolean }[] = [];
  return {
    m,
    maps,
    relief,
    facades,
    facadeMaterial,
    leaves,
    pennant,
    gable,
    botanical,
    contact,
    glowMap,
    windows,
    geo,
    uniforms,
    shared<T extends THREE.Material | THREE.Texture | THREE.BufferGeometry>(key: string, make: () => T): T {
      const known = sharedThings.get(key);
      if (known !== undefined) return known as T;
      const made = make();
      sharedThings.set(key, made);
      return made;
    },
    derive(key, base, setup) {
      const known = sharedThings.get(key);
      if (known instanceof THREE.MeshStandardMaterial) return known;
      const material = base.clone();
      material.name = key;
      setup(material);
      derived.push({ base, material, color: material.color.equals(base.color), bump: material.bumpScale === base.bumpScale });
      sharedThings.set(key, material);
      return material;
    },
    shimmerHook,
    hipRoof(material) {
      let mm = hipRoofs.get(material);
      if (mm === undefined) {
        mm = keep(material.clone());
        mm.side = THREE.DoubleSide;
        hipRoofs.set(material, mm);
      }
      return mm;
    },
    applyArtwork(slot, image) {
      // A new image of another size needs new GPU storage: free the old upload first (three re-uploads on use).
      if (slot === 'caustics') {
        caustics.dispose();
        caustics.image = image;
        caustics.needsUpdate = true;
        artwork.caustics = true;
        return;
      }
      const kind = artworkSlots[slot];
      const map = maps[kind];
      map.dispose();
      relief[kind].dispose();
      map.image = image;
      if (slot === 'rug') {
        // The rug ships as its bottom-left quarter: mirrored over two repeats (`artwork.js` `mirrorQuarter`).
        for (const t of [map, relief[kind]]) {
          t.wrapS = THREE.MirroredRepeatWrapping;
          t.wrapT = THREE.MirroredRepeatWrapping;
          t.repeat.set(2, 2);
        }
      }
      map.needsUpdate = true;
      paintRelief(relief[kind], image);
      if (slot === 'limestone') {
        m.stone.color.set(c.stoneArtwork);
        m.stone.bumpScale = look.bump.stoneArtwork;
      }
      // Derived finishes follow their base where they did not set their own.
      for (const d of derived) {
        if (d.color) d.material.color.copy(d.base.color);
        if (d.bump) d.material.bumpScale = d.base.bumpScale;
      }
      artwork[slot] = true;
    },
    artwork: () => ({ ...artwork }),
    dispose() {
      for (const x of owned) x.dispose();
      for (const x of sharedThings.values()) x.dispose();
      sharedThings.clear();
      hipRoofs.clear();
    },
  };
};

/** The helpers a prefab builds with, bound to a kit, a seed and the placement's parameters. */
export const contextFor = (kit: Kit, seed: number, params: Readonly<Record<string, number | boolean | string>> = {}): Ctx => {
  const random = seededRandom(seed);
  const { m, geo } = kit;
  const unitOf = (map: THREE.Texture | null): number | null => {
    const w = look.worldSize;
    if (map === kit.maps.cobble) return w.paving;
    if (map === kit.maps.stone) return w.masonry;
    if (map === kit.maps.roof) return w.terracotta;
    if (map === kit.maps.slate) return w.slate;
    return Object.values(kit.maps).includes(map as THREE.CanvasTexture) ? w.other : null;
  };
  const mesh: Ctx['mesh'] = (parent, geometry, material, x = 0, y = 0, z = 0, sx = 1, sy = 1, sz = 1) => {
    let g = geometry;
    const map = 'map' in material ? (material.map as THREE.Texture | null) : null;
    const unit = g === geo.cube ? unitOf(map) : null;
    if (unit !== null) {
      // World-scaled UVs on a stretched cube: a texture keeps its texel density on every face.
      g = g.clone();
      const uv = g.getAttribute('uv');
      const n = g.getAttribute('normal');
      for (let i = 0; i < uv.count; i += 1) {
        const nx = Math.abs(n.getX(i));
        const ny = Math.abs(n.getY(i));
        uv.setXY(i, (uv.getX(i) * (nx > 0.5 ? sz : sx)) / unit, (uv.getY(i) * (ny > 0.5 ? sz : sy)) / unit);
      }
    }
    if (material === m.windowGlass || material === m.windowCool) {
      // One of the atlas's four panes, picked by where the window stands.
      g = g.clone();
      const uv = g.getAttribute('uv');
      const tile = Math.abs(Math.round(x * 19 + y * 31 + z * 13)) % 4;
      for (let i = 0; i < uv.count; i += 1) uv.setXY(i, ((tile % 2) + 0.025 + uv.getX(i) * 0.95) / 2, (Math.floor(tile / 2) + 0.02 + uv.getY(i) * 0.96) / 2);
    }
    const o = new THREE.Mesh(g, material);
    o.position.set(x, y, z);
    o.scale.set(sx, sy, sz);
    o.castShadow = true;
    o.receiveShadow = true;
    parent.add(o);
    return o;
  };
  const contact = (parent: THREE.Object3D, x: number, z: number, width: number, depth: number): void => {
    const g = new THREE.PlaneGeometry(width + 1, depth + 1);
    g.rotateX(-Math.PI / 2);
    g.translate(x, look.contactY, z);
    const list = (parent.userData['contacts'] as THREE.BufferGeometry[] | undefined) ?? [];
    list.push(g);
    parent.userData['contacts'] = list;
  };
  const box: Ctx['box'] = (parent, x, y, z, w, h, d, material = m.stone) => {
    if (y - h / 2 < 0.13 && y - h / 2 > -0.1 && h > 0.4 && w < 9 && d < 8 && w > 0.25 && d > 0.25) contact(parent, x, z, w, d);
    return mesh(parent, geo.cube, material, x, y, z, w, h, d);
  };
  const cylinder: Ctx['cylinder'] = (parent, x, y, z, rad, h, material = m.stone, top = 1) => {
    if (y - h / 2 < 0.13 && y - h / 2 > -0.1 && h > 0.3 && rad < 2) contact(parent, x, z, rad * 2, rad * 2);
    const g = top === 1 ? geo.cyl : kit.shared(`cylinder:${top}`, () => new THREE.CylinderGeometry(top, 1, 1, 12));
    return mesh(parent, g, material, x, y, z, rad, h, rad);
  };
  const up = new THREE.Vector3(0, 1, 0);
  const line: Ctx['line'] = (parent, a, b, width, material = m.darkWood) => {
    const aa = new THREE.Vector3(...a);
    const bb = new THREE.Vector3(...b);
    const d = bb.clone().sub(aa);
    const mid = aa.clone().add(bb).multiplyScalar(0.5);
    const o = box(parent, mid.x, mid.y, mid.z, width, d.length(), width, material);
    o.quaternion.setFromUnitVectors(up, d.normalize());
    return o;
  };
  const glow: Ctx['glow'] = (parent, x, y, z, size = 1.8, opacity = look.glowOpacity) => {
    const marker = new THREE.Object3D();
    marker.position.set(x, y, z);
    marker.userData['glow'] = { size, opacity };
    parent.add(marker);
  };
  const lantern: Ctx['lantern'] = (p, x, y, z, post = false) => {
    if (post) {
      cylinder(p, x, (y - 0.29) / 2, z, 0.055, y - 0.29, m.iron, 0.75);
      cylinder(p, x, 0.16, z, 0.13, 0.32, m.iron, 0.75);
      cylinder(p, x, 0.34, z, 0.08, 0.075, m.brass);
      cylinder(p, x, y - 0.33, z, 0.1, 0.08, m.iron);
    }
    const lower = 0.115;
    const upper = 0.17;
    const bottom = y - 0.2;
    const top = y + 0.22;
    for (let side = 0; side < 4; side += 1) {
      const angle = (side * Math.PI) / 2;
      const turn = (xx: number, yy: number, zz: number): Vec3 => [x + xx * Math.cos(angle) + zz * Math.sin(angle), yy, z - xx * Math.sin(angle) + zz * Math.cos(angle)];
      const points = [turn(-lower, bottom, lower), turn(lower, bottom, lower), turn(upper, top, upper), turn(-upper, top, upper)];
      const pane = new THREE.BufferGeometry();
      pane.setAttribute('position', new THREE.Float32BufferAttribute(points.flat(), 3));
      pane.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
      const shade = [1, 0.78, 0.68, 0.85][side] ?? 1;
      pane.setAttribute('color', new THREE.Float32BufferAttribute(Array.from({ length: 12 }, () => shade), 3));
      pane.setIndex([0, 1, 2, 0, 2, 3]);
      pane.computeVertexNormals();
      mesh(p, pane, m.lanternGlass, 0, 0, 0);
    }
    box(p, x, y - 0.23, z, 0.29, 0.055, 0.29, m.brass);
    box(p, x, y + 0.235, z, 0.39, 0.055, 0.39, m.iron);
    for (const a of [-1, 1]) for (const b of [-1, 1]) line(p, [x + a * lower, bottom - 0.02, z + b * lower], [x + a * upper, top + 0.02, z + b * upper], 0.026, m.iron);
    const cap = mesh(p, kit.shared('lantern-cap', () => new THREE.CylinderGeometry(0.025, 0.31, 0.19, 4)), m.iron, x, y + 0.36, z);
    cap.rotation.y = Math.PI / 4;
    cylinder(p, x, y + 0.49, z, 0.026, 0.095, m.brass);
    mesh(p, geo.sphere, m.brass, x, y + 0.55, z, 0.04, 0.05, 0.04);
    glow(p, x, y, z, 1.25, look.lanternGlowOpacity);
  };
  const archShape: Ctx['archShape'] = (width, height) => {
    const shape = new THREE.Shape();
    shape.moveTo(-width / 2, -height / 2);
    shape.lineTo(width / 2, -height / 2);
    shape.lineTo(width / 2, height / 2 - width / 2);
    shape.absarc(0, height / 2 - width / 2, width / 2, 0, Math.PI, false);
    shape.lineTo(-width / 2, -height / 2);
    return shape;
  };
  const windowFrame: Ctx['windowFrame'] = (p, x, y, z, width = 0.8, height = 1.12, shutters = true, glass = m.windowGlass) => {
    if (height > 1.1) {
      const rim = new THREE.ExtrudeGeometry(archShape(width + 0.22, height + 0.2), { depth: 0.14, bevelEnabled: false, curveSegments: 8 });
      mesh(p, rim, m.stone, x, y, z);
      const pane = new THREE.ShapeGeometry(archShape(width, height), 8);
      const uv = pane.getAttribute('uv');
      for (let i = 0; i < uv.count; i += 1) uv.setXY(i, uv.getX(i) / width + 0.5, uv.getY(i) / height + 0.5);
      mesh(p, pane, glass, x, y, z + 0.151);
      for (let i = 0; i < 9; i += 1) {
        const a = (i * Math.PI) / 8;
        const stone = box(p, x + Math.cos(a) * (width / 2 + 0.075), y + height / 2 - width / 2 + Math.sin(a) * (width / 2 + 0.075), z + 0.1, 0.15, 0.17, 0.23, m.cream);
        stone.rotation.z = a - Math.PI / 2;
      }
    } else {
      box(p, x, y, z, width + 0.2, height + 0.18, 0.16, m.darkWood);
      box(p, x, y, z + 0.1, width, height, 0.07, glass);
    }
    box(p, x, y, z + 0.16, 0.045, height, 0.06, m.darkWood);
    box(p, x, y, z + 0.16, width, 0.045, 0.06, m.darkWood);
    box(p, x, y - height / 2 - 0.12, z + 0.18, width + 0.32, 0.13, 0.35, m.stone);
    if (shutters) {
      for (const s of [-1, 1]) {
        box(p, x + s * (width * 0.73), y, z + 0.09, width * 0.33, height + 0.08, 0.12, m.teal);
        for (let yy = -height * 0.4; yy < height * 0.45; yy += 0.13) box(p, x + s * (width * 0.73), y + yy, z + 0.17, width * 0.33, 0.035, 0.06, m.darkWood);
      }
    }
  };
  const roof: Ctx['roof'] = (p, x, y, z, width, depth, rise, material = m.roof) => {
    const slope = Math.sqrt((depth / 2) ** 2 + rise * rise);
    for (const s of [-1, 1]) {
      const o = box(p, x, y + rise / 2, z + (s * depth) / 4, width + 0.55, 0.16, slope + 0.2, material);
      o.rotation.x = s * Math.atan2(rise, depth / 2);
      box(p, x, y - 0.05, z + s * (depth / 2 + 0.1), width + 0.6, 0.17, 0.18, m.darkWood);
    }
    cylinder(p, x, y + rise + 0.09, z, 0.1, width + 0.7, material).rotation.z = Math.PI / 2;
    for (const s of [-1, 1]) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute([x + (s * width) / 2, y, z - depth / 2, x + (s * width) / 2, y, z + depth / 2, x + (s * width) / 2, y + rise, z], 3));
      g.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 0.5, 1], 2));
      g.computeVertexNormals();
      mesh(p, g, kit.gable, 0, 0, 0);
      line(p, [x + s * (width / 2 + 0.02), y, z - depth / 2], [x + s * (width / 2 + 0.02), y + rise, z], 0.13);
      line(p, [x + s * (width / 2 + 0.02), y, z + depth / 2], [x + s * (width / 2 + 0.02), y + rise, z], 0.13);
    }
  };
  const hipRoof: Ctx['hipRoof'] = (p, x, y, z, width, depth, rise, material) => {
    const a: Vec3 = [-width / 2, 0, -depth / 2];
    const b: Vec3 = [width / 2, 0, -depth / 2];
    const c2: Vec3 = [width / 2, 0, depth / 2];
    const d: Vec3 = [-width / 2, 0, depth / 2];
    const e: Vec3 = [-width * 0.21, rise, 0];
    const f: Vec3 = [width * 0.21, rise, 0];
    const faces: readonly (readonly Vec3[])[] = [
      [a, b, f],
      [a, f, e],
      [d, e, f],
      [d, f, c2],
      [a, e, d],
      [b, c2, f],
    ];
    const positions: number[] = [];
    const uv: number[] = [];
    faces.forEach((face, faceIndex) => {
      for (const v of face) {
        positions.push(v[0] + x, v[1] + y, v[2] + z);
        const frontOrBack = faceIndex < 4;
        const alongEave = frontOrBack ? v[0] + width / 2 : v[2] + depth / 2;
        const run = frontOrBack ? depth / 2 : width * 0.29;
        const alongSlope = (v[1] / rise) * Math.hypot(run, rise);
        uv.push(alongEave / 3, alongSlope / 3);
      }
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.computeVertexNormals();
    mesh(p, g, kit.hipRoof(material), 0, 0, 0);
    for (const [u, v] of [
      [a, b],
      [b, c2],
      [c2, d],
      [d, a],
      [e, f],
      [a, e],
      [d, e],
      [b, f],
      [c2, f],
    ] as const) {
      line(p, [u[0] + x, u[1] + y, u[2] + z], [v[0] + x, v[1] + y, v[2] + z], 0.1, material);
    }
  };
  const flowers: Ctx['flowers'] = (p, x, y, z, width = 1, count = 20, trailing = true) => {
    const seed = Math.abs(Math.round(x * 193 + y * 977 + z * 317)) + 1;
    const plant = floweringPlant(p, kit.botanical, width, count, seed, trailing);
    plant.position.set(x, y, z);
  };
  const pot: Ctx['pot'] = (p, x, z, y = 0, scale = 0.6) => {
    const vessel = new THREE.Group();
    vessel.position.set(x, y, z);
    p.add(vessel);
    terracottaPlanter(vessel, scale, m.red, m.dark);
    flowers(p, x, y + scale * 0.62, z, scale * 0.78, 20, 0.6);
  };
  const barrel: Ctx['barrel'] = (p, x, z, size = 0.55, y = 0) => {
    cylinder(p, x, y + size * 0.55, z, size * 0.48, size * 1.1, m.wood, 0.83);
    for (const yy of [0.18, 0.8]) {
      const torus = kit.shared(`barrel-hoop:${size}`, () => new THREE.TorusGeometry(size * 0.47, 0.025, 4, 12));
      const o = mesh(p, torus, m.iron, x, y + yy * size, z);
      o.rotation.x = Math.PI / 2;
    }
    cylinder(p, x, y + size * 1.11, z, size * 0.41, 0.045, m.darkWood);
  };
  const crate: Ctx['crate'] = (p, x, z, size = 0.7, y = 0) => {
    box(p, x, y + size / 2, z, size, size, size, m.wood);
    for (const s of [-1, 1]) {
      box(p, x + s * size * 0.4, y + size / 2, z + size / 2 + 0.015, 0.07, size, 0.06, m.darkWood);
      box(p, x, y + size / 2 + s * size * 0.4, z + size / 2 + 0.015, size, 0.07, 0.06, m.darkWood);
    }
    line(p, [x - size * 0.38, y + 0.1, z + size / 2 + 0.04], [x + size * 0.38, y + size - 0.1, z + size / 2 + 0.04], 0.07, m.darkWood);
  };
  return {
    kit,
    m,
    random,
    params,
    plane: geo.plane,
    sphere: geo.sphere,
    cube: geo.cube,
    cyl: geo.cyl,
    leafMats: kit.leaves,
    mesh,
    box,
    cylinder,
    line,
    glow,
    lantern,
    windowFrame,
    roof,
    hipRoof,
    archShape,
    flowers,
    pot,
    barrel,
    crate,
    cutaway(o) {
      o.traverse((x) => {
        x.userData['cutaway'] = true;
      });
    },
  };
};

/** A numeric parameter of a placement, or its default. */
export const num = (ctx: Ctx, key: string, fallback: number): number => {
  const v = ctx.params[key];
  return typeof v === 'number' ? v : fallback;
};

/** A boolean parameter of a placement, or its default. */
export const flag = (ctx: Ctx, key: string, fallback: boolean): boolean => {
  const v = ctx.params[key];
  return typeof v === 'boolean' ? v : fallback;
};
